import { format } from 'date-fns-tz'

import { getPricesFromTimestamps } from '../../ClientReservations/Amenities/helpers'

import { ModifiedPriceType } from './types'

const isPriceOverridden = (advancedSettings) => {
  const { modifyPrice, modifyPriceType: type, modifyPriceValue: value } = advancedSettings
  return (
    !!modifyPrice &&
    type === ModifiedPriceType.CustomPrice &&
    (!!value || value === 0 || value === '0')
  )
}

const applyDiscount = (basePrice, advancedSettings = {}) => {
  const { modifyPrice, modifyPriceType, modifyPriceValue } = advancedSettings
  if (!modifyPrice || !modifyPriceValue) {
    return basePrice
  }
  if (modifyPrice && modifyPriceType === ModifiedPriceType.Discount) {
    return basePrice - Number(modifyPriceValue)
  } else if (modifyPrice && modifyPriceType === ModifiedPriceType.DiscountPercentage) {
    return (basePrice - (basePrice * (Number(modifyPriceValue) / 100) * 100) / 100).toFixed(2)
  }
  return basePrice
}

const calculateMultidayBookingBasePrice = (amenity, calendarValues, timezone) => {
  const { multidaySlots, price } = amenity
  const reservationDates = [calendarValues[0]]
  const endDateFormatted = format(calendarValues[1], 'yyyy-MM-dd')
  let nextDate = new Date(calendarValues[0])
  nextDate.setDate(nextDate.getDate() + 1)
  while (format(nextDate, 'yyyy-MM-dd') !== endDateFormatted) {
    reservationDates.push(nextDate)
    nextDate = new Date(nextDate)
    nextDate.setDate(nextDate.getDate() + 1)
  }
  return (
    reservationDates.reduce(
      (totalPrice, date) =>
        totalPrice + getPricesFromTimestamps(date, multidaySlots, price, timezone).price,
      0,
    ) + (amenity.extraFee || 0)
  )
}

export const getHourlySlotsForTime = (amenity, selectedDay, startTime, endTime) => {
  return amenity.hourlySlots.filter(
    (slot) =>
      slot.weekdays.includes(selectedDay) &&
      ((slot?.startTime <= startTime && slot?.endTime > startTime) ||
        (slot?.startTime < endTime && slot?.endTime >= endTime)),
  )
}

const calculateBasePrice = (amenity, selectedTime, calendarValues) => {
  const selectedDay = new Date(calendarValues[0]).getDay()
  const { timeInterval } = amenity
  const { startTime, endTime } = selectedTime

  // If no endTime, it's a single slot
  if (!endTime) {
    const slots = getHourlySlotsForTime(amenity, selectedDay, startTime, startTime + timeInterval)
    const price = slots.reduce((totalPrice, slot) => totalPrice + slot.price, 0)
    return price + (amenity.extraFee || 0)
  }

  // Calculate price for each time slot in the range
  let totalPrice = 0
  let currentTime = startTime

  while (currentTime <= endTime) {
    const slotEndTime = currentTime + timeInterval
    const slots = getHourlySlotsForTime(amenity, selectedDay, currentTime, slotEndTime)

    // Get the price for this specific time slot
    const slotPrice = slots.reduce((sum, slot) => sum + slot.price, 0)
    totalPrice += slotPrice

    // Move to next time slot
    currentTime += timeInterval
  }

  return totalPrice + (amenity.extraFee || 0)
}

export const calculateFinalPrice = (
  amenity,
  calendarValues,
  selectedTime,
  timezone,
  advancedSettings = {},
) => {
  if (isPriceOverridden(advancedSettings)) {
    return Number(advancedSettings.modifyPriceValue)
  }
  // Note: a multi-day amenity's price can't be computed until both ends of the range are
  // picked - calendarValues[1] is undefined while only the start date is selected, which
  // would otherwise crash date-fns's format() with "Invalid time value" below.
  if (amenity.multiDayBooking && !calendarValues?.[1]) {
    return 0
  }
  const basePrice = amenity.multiDayBooking
    ? calculateMultidayBookingBasePrice(amenity, calendarValues, timezone)
    : calculateBasePrice(amenity, selectedTime, calendarValues)
  return Math.max(applyDiscount(basePrice.toFixed(2), advancedSettings), 0)
}

const formatHourlyDuration = (minutes, t) => {
  if (minutes >= 59) {
    const hours = minutes / 60
    const value = Number.isInteger(hours) ? hours : Number(hours.toFixed(2))
    return t('reservations:bookingDurationLimits.hoursCount', { count: value })
  }
  return t('reservations:bookingDurationLimits.minutesCount', { count: minutes })
}

export const getReservationDurationLimits = (amenity, t) => {
  if (!amenity) return null

  const { maxTime, maximumDays, minTime, minimumDays, multiDayBooking } = amenity

  if (multiDayBooking) {
    return {
      max: t('reservations:bookingDurationLimits.daysCount', { count: Number(maximumDays) }),
      min: t('reservations:bookingDurationLimits.daysCount', { count: Number(minimumDays) }),
    }
  }

  return {
    max: formatHourlyDuration(Number(maxTime), t),
    min: formatHourlyDuration(Number(minTime), t),
  }
}

export const calculateDaysBetweenDates = (datesArray) => {
  const [startDate, endDate] = datesArray
  const timeDifference = endDate.getTime() - startDate.getTime()
  const daysDifference = timeDifference / (1000 * 3600 * 24)
  const days = Math.max(Math.ceil(daysDifference) - 1, 0)
  return days
}

export const filterAvailablePlacesByCapacity = (timeSlots, amenity) => {
  if (!amenity?.locations || !timeSlots?.length) {
    return []
  }

  // Create a map to track total capacity for each location across all time slots
  const locationCapacityMap = {}

  // Initialize capacity map with all locations
  amenity.locations.forEach((location) => {
    locationCapacityMap[location.id] = 0
  })

  // Sum up capacity for each location across all time slots
  timeSlots.forEach((timeSlot) => {
    if (timeSlot.locations && Array.isArray(timeSlot.locations)) {
      timeSlot.locations.forEach((locationData) => {
        if (
          Object.prototype.hasOwnProperty.call(locationCapacityMap, locationData.id) &&
          !locationData.isBlocked
        ) {
          locationCapacityMap[locationData.id] += locationData.capacity || 0
        }
      })
    }
  })

  // Filter out locations that have 0 total capacity across all time slots
  const availableIds = Object.keys(locationCapacityMap)
    .filter((locationId) => locationCapacityMap[locationId] > 0)
    .map((id) => parseInt(id))

  return availableIds
}

export const getFilteredTimeSlotsForLocation = (locationId, timeSlots = []) => {
  if (!timeSlots || !timeSlots.length || !locationId) return []

  return timeSlots
    .filter((timeSlot) => {
      if (!timeSlot.locations || !Array.isArray(timeSlot.locations)) return false

      const locationData = timeSlot.locations.find((loc) => loc.id === locationId)
      return locationData && !locationData.isBlocked
    })
    .map((timeSlot) => ({
      startTime: timeSlot.startTime,
      reservations: timeSlot.reservations || 0,
      capacity: timeSlot.locations.find((loc) => loc.id === locationId)?.capacity || 0,
      locations: timeSlot.locations,
      price: timeSlot.price,
    }))
}
